import { Component, ErrorInfo, ReactNode } from 'react';
import {
  isChunkLoadError,
  handleChunkLoadError,
} from '@/utils/chunk-error-handler';
import { Button } from '@ygoargentina/ui/components/custom/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ygoargentina/ui/components/card';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * Error Boundary that catches chunk loading errors and offers to reload the page
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    const isChunk = isChunkLoadError(error);

    // If it's a chunk error, trigger automatic reload
    if (isChunk) {
      handleChunkLoadError(error);
    }

    return {
      hasError: true,
      error,
      isChunkError: isChunk,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);

    // If it's a chunk error, the handleChunkLoadError was already called in getDerivedStateFromError
    // This will reload the page automatically
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // If it's a chunk error, show a loading state while reloading
      if (this.state.isChunkError) {
        return (
          <div className="flex items-center justify-center min-h-screen p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <CardTitle>Actualizando aplicación...</CardTitle>
                </div>
                <CardDescription>
                  Se está cargando la última versión de la aplicación.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        );
      }

      // For other errors, show a generic error message
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <CardTitle>Algo salió mal</CardTitle>
              </div>
              <CardDescription>
                Ha ocurrido un error inesperado. Por favor, intenta recargar la
                página.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {this.state.error && (
                  <details className="text-sm text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">
                      Ver detalles del error
                    </summary>
                    <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                      {this.state.error.message}
                      {this.state.error.stack &&
                        `\n\n${this.state.error.stack}`}
                    </pre>
                  </details>
                )}
                <Button onClick={this.handleReload} className="w-full">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Recargar página
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
